import { VAST, Wrapper } from '@lib/iab/iab-vast-model';
import parse from '@lib/iab/iab-vast-parser';
import EventEmitter from 'eventemitter3';

import { atob } from './atob';
import { HTTPError } from './http-error';
import { VASTLoaderError } from './loader-error';
import {
    VASTLoaderOptions,
    FetchResponse,
    VASTLoaderEvents,
    TimeoutPromise,
} from './types';

const RE_DATA_URI = /^data:(.*?)(;\s*base64)?,(.*)/;

const DEFAULT_OPTIONS: Required<VASTLoaderOptions> = {
    maxDepth: 10,
    credentials: 'omit',
    timeout: 10000,
    noSingleAdPods: false,
    prepareUri: (uri: string) => uri,
};

export class VASTLoader extends EventEmitter {
    private _uri: string;
    private _root: VASTLoader;
    private _options: Required<VASTLoaderOptions>;
    private _depth: number;
    private _fetch: typeof fetch;

    constructor(uri: string, options?: VASTLoaderOptions, parent?: VASTLoader) {
        super();
        this._uri = uri;
        if (parent != null) {
            this._root = parent._root;
            this._options = this._root._options;
            this._depth = parent._depth + 1;
        } else {
            this._root = this;
            this._options = Object.assign({}, DEFAULT_OPTIONS, options);
            this._depth = 1;
        }
        this._fetch = fetch.bind(window);
    }

    public load(): Promise<VAST[]> {
        return this._load(this._uri).catch((error: Error) => {
            this._emit('error', { error });
            throw error;
        });
    }

    private _load(uri: string): Promise<VAST[]> {
        return Promise.resolve()
            .then(() => {
                this._emit('willFetch', { uri });
                const match = RE_DATA_URI.exec(uri);
                return match == null
                    ? this._fetchUri(uri)
                    : this._parseDataUri(match[3], match[1], match[2] != null);
            })
            .then(({ headers, body }: FetchResponse) => {
                this._emit('didFetch', { uri, headers, body });
                this._emit('willParse', { uri, headers, body });
                const vast = parse(body, {
                    noSingleAdPods: this._options.noSingleAdPods,
                }) as VAST;
                vast.uri = uri;
                this._emit('didParse', { uri, headers, body, vast });
                if (vast.ads.length > 0) {
                    const ad = vast.ads.get(0);
                    if (
                        ad.$type === 'Wrapper' &&
                        ad instanceof Wrapper &&
                        ad.vastAdTagURI
                    ) {
                        return this._loadWrapped(ad.vastAdTagURI, vast);
                    }
                } else if (this._depth > 1) {
                    throw new VASTLoaderError(303, null, uri);
                }
                return [vast];
            });
    }

    private _parseDataUri(
        data: string,
        mimeType: string,
        isBase64: boolean
    ): FetchResponse {
        return {
            headers: new Headers(),
            body: isBase64 ? atob(data) : decodeURIComponent(data),
        };
    }

    private _loadWrapped(vastAdTagURI: string, vast: VAST): Promise<VAST[]> {
        return Promise.resolve()
            .then(() => {
                const { maxDepth } = this._options;
                if (maxDepth > 0 && this._depth + 1 >= maxDepth) {
                    throw new VASTLoaderError(302, null, vastAdTagURI);
                }
                const childLoader = new VASTLoader(
                    vastAdTagURI,
                    undefined,
                    this
                );
                return childLoader.load();
            })
            .then((children: VAST[]) => [vast, ...children]);
    }

    private _fetchUri(uri: string): Promise<FetchResponse> {
        const preparedUri = this._options.prepareUri(uri);
        const fetching = this._fetchWithCredentials(preparedUri);
        const timingOut = this._createTimeouter(fetching, preparedUri);
        return Promise.race([fetching, timingOut])
            .then((response: Response) => {
                timingOut.cancel();
                return response.text().then((body: string) => ({
                    headers: response.headers,
                    body,
                }));
            })
            .catch((err: Error) => {
                timingOut.cancel();
                if (this._depth > 1) {
                    throw new VASTLoaderError(301, null, preparedUri);
                }
                throw err;
            });
    }

    private _fetchWithCredentials(uri: string): Promise<Response> {
        let { credentials } = this._options;
        if (typeof credentials === 'function') {
            credentials = credentials(uri);
        }
        const credentialsArray = Array.isArray(credentials)
            ? credentials
            : [credentials];

        return credentialsArray.reduce(
            (prev: Promise<Response>, cred: string) =>
                prev.catch(() => this._tryFetch(uri, cred)),
            Promise.reject(new Error())
        );
    }

    private _tryFetch(uri: string, credentials: string): Promise<Response> {
        return this._root
            ._fetch(uri, { credentials: credentials as RequestCredentials })
            .then((response: Response) => {
                if (!response.ok) {
                    const httpError = new HTTPError(
                        response.status,
                        response.statusText
                    );
                    throw new VASTLoaderError(301, httpError, uri);
                }
                return response;
            })
            .catch((error: Error) => {
                this._emit('fetchError', { uri, credentials, error });
                throw error;
            });
    }

    private _createTimeouter(
        fetching: Promise<Response>,
        uri: string
    ): TimeoutPromise {
        const ms = this._options.timeout;
        let timeout: NodeJS.Timeout | null = null;
        const timingOut = new Promise<never>((resolve, reject) => {
            timeout = setTimeout(() => {
                reject(new VASTLoaderError(301, null, uri));
            }, ms);
        }) as TimeoutPromise;

        timingOut.cancel = () => {
            if (timeout != null) {
                clearTimeout(timeout);
                timeout = null;
            }
        };

        return timingOut;
    }

    private _emit<K extends keyof VASTLoaderEvents>(
        event: K,
        data: VASTLoaderEvents[K]
    ): void {
        console.log('emit', event, data);
        this._root.emit(event, data);
    }
}
